import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai/react';
import { faAnglesRight, faGear, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faComment, faStar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import { AIAssistantChat } from './AIAssistantChat';
import { AIAssistantSettings } from './AIAssistantSettings';
import { AIAssistantSkillsPage } from './AIAssistantSkillsPage';
import type { GroupId, ViewModelId } from 'balus-common';
import { aiAssistantDrawerOpenAtom } from './aiAssistantDrawerAtom';
import { useStartNewAIAssistantChat } from './useStartNewAIAssistantChat';
import { useAIAssistantRoute, useAIAssistantRouter, useAIAssistantThreadRequest } from './aiAssistantRouteAtom';
import { AIAssistantChatList } from './AIAssistantChatList';

const TAB_ACTIVE_CLASS = 'relative z-10 border-teal-500 bg-teal-50 font-bold text-teal-700';
const TAB_INACTIVE_CLASS = 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50';

export function AIAssistantContent({ viewModelId, groupId }: { viewModelId: ViewModelId; groupId: GroupId }) {
  const { t } = useTranslation();
  const route = useAIAssistantRoute(viewModelId);
  const router = useAIAssistantRouter(viewModelId);
  const threadRequest = useAIAssistantThreadRequest(viewModelId);
  const setDrawerOpen = useSetAtom(aiAssistantDrawerOpenAtom);
  const startNewChat = useStartNewAIAssistantChat(viewModelId);

  // エージェントタブはスキルページ以外（一覧・チャット・設定）で選択中の扱い
  const isAgentTabActive = route.page !== 'skill';

  return (
    <div className="pointer-events-auto flex min-h-0 w-full grow flex-col items-stretch justify-stretch bg-white px-4 shadow-md">
      <div className="flex items-center gap-2 pb-2 pt-3">
        <div className="flex grow">
          <button
            className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-l-md border px-3 py-1.5 text-sm ${
              isAgentTabActive ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS
            }`}
            onClick={() => {
              if (!isAgentTabActive) router.push({ page: 'list' });
            }}
          >
            <FontAwesomeIcon icon={faComment} />
            {t('viewModel.aiChat.agentTabLabel')}
          </button>
          <button
            className={`-ml-px flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-r-md border px-3 py-1.5 text-sm ${
              isAgentTabActive ? TAB_INACTIVE_CLASS : TAB_ACTIVE_CLASS
            }`}
            onClick={() => {
              if (isAgentTabActive) router.push({ page: 'skill' });
            }}
          >
            <FontAwesomeIcon icon={faStar} />
            {t('viewModel.aiChat.skillTabLabel')}
          </button>
        </div>
        <button
          className="px-1 text-gray-700 hover:text-gray-900"
          title={t('common.action.close')}
          aria-label={t('common.action.close')}
          onClick={() => setDrawerOpen(false)}
        >
          <FontAwesomeIcon icon={faAnglesRight} />
        </button>
      </div>

      {route.page === 'list' && (
        <div className="flex items-center gap-3 py-3">
          <h2 className="text-base font-bold">{t('viewModel.aiChat.title')}</h2>
          <div className="grow" />
          <DarkTooltip tooltip={t('viewModel.aiChat.newChat')} placement="bottom">
            <button
              className="text-sm text-gray-700 hover:text-gray-900"
              aria-label={t('viewModel.aiChat.newChat')}
              onClick={startNewChat}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </DarkTooltip>
          <DarkTooltip tooltip={t('viewModel.aiSettings.title')} placement="bottom">
            <button
              className="text-sm text-gray-700 hover:text-gray-900"
              aria-label={t('viewModel.aiSettings.title')}
              onClick={() => {
                router.push({ page: 'settings' });
              }}
            >
              <FontAwesomeIcon icon={faGear} />
            </button>
          </DarkTooltip>
        </div>
      )}

      {/* 設定画面の表示中もチャットの状態（入力中テキストやストリーミング）を保持するため、
          アンマウントせず hidden で切り替える。
          一覧からスレッドを選択したときは key で再マウントし、選択スレッドを読み込み直す */}
      <div className={`contents ${route.page === 'chat' ? '' : 'hidden'}`}>
        <AIAssistantChat
          key={threadRequest === null ? 'latest' : `${threadRequest.threadId}:${threadRequest.nonce}`}
          viewModelId={viewModelId}
          groupId={groupId}
          requestedThreadId={threadRequest?.threadId}
        />
      </div>

      <div className={`contents ${route.page === 'settings' ? '' : 'hidden'}`}>
        <AIAssistantSettings viewModelId={viewModelId} />
      </div>

      <div className={`contents ${route.page === 'list' ? '' : 'hidden'}`}>
        <AIAssistantChatList viewModelId={viewModelId} />
      </div>

      <div className={`contents ${route.page === 'skill' ? '' : 'hidden'}`}>
        <AIAssistantSkillsPage viewModelId={viewModelId} />
      </div>
    </div>
  );
}
